import React, { useState } from "react";
import { useUser } from "@clerk/react";
import { ArrowLeft, User, Lock, Trash2 } from "lucide-react";

const font = "'Inter', -apple-system, BlinkMacSystemFont, sans-serif";

const T = {
  nav:         "#232231",
  navBorder:   "#2d2c3f",
  navText:     "#9ca3af",
  navActive:   "#f9fafb",
  bg:          "#f9fafb",
  surface:     "#ffffff",
  border:      "#e5e7eb",
  text:        "#111827",
  muted:       "#6b7280",
  accent:      "#232231",
  accentLight: "#eeeef2",
  danger:      "#dc2626",
  dangerLight: "#fee2e2",
  shadow:      "0 1px 2px rgba(0,0,0,0.06)",
  r:           "8px",
  rLg:         "12px",
};

const inp = {
  display:"block", width:"100%", boxSizing:"border-box",
  padding:"8px 11px", fontSize:14, lineHeight:1.5,
  border:`1px solid ${T.border}`, borderRadius:T.r,
  background:T.surface, color:T.text, outline:"none", fontFamily:font,
};

const lbl = {
  display:"block", fontSize:12, fontWeight:600, color:T.muted,
  marginBottom:5, letterSpacing:"0.02em", textTransform:"uppercase", fontFamily:font,
};

const btnPrimary = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"8px 16px", fontSize:13, fontWeight:600,
  background:T.accent, color:"#fff",
  border:"none", borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

const btnOutline = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"7px 13px", fontSize:13, fontWeight:500,
  background:"transparent", color:T.navActive,
  border:"1px solid #374151", borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

const btnDanger = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"8px 16px", fontSize:13, fontWeight:600,
  background:"transparent", color:T.danger,
  border:"1px solid #fca5a5", borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

const btnCancel = {
  display:"inline-flex", alignItems:"center", gap:6,
  padding:"7px 13px", fontSize:13, fontWeight:500,
  background:"transparent", color:T.muted,
  border:`1px solid ${T.border}`, borderRadius:T.r, cursor:"pointer", fontFamily:font,
};

function ReadField({ label, value }) {
  return (
    <div style={{ marginBottom:14 }}>
      <span style={lbl}>{label}</span>
      <div style={{ padding:"9px 12px", fontSize:14, background:T.bg,
        border:`1px solid ${T.border}`, borderRadius:T.r, color:T.text, fontFamily:font }}>
        {value || <span style={{ color:T.muted }}>—</span>}
      </div>
    </div>
  );
}

function StatusMsg({ type, msg }) {
  if (!msg) return null;
  const ok = type === "success";
  return (
    <div style={{ padding:"9px 14px", borderRadius:T.r, fontSize:13, fontFamily:font, marginTop:12,
      background: ok ? "#f0fdf4" : "#fef2f2",
      border: ok ? "1px solid #bbf7d0" : "1px solid #fecaca",
      color: ok ? "#166534" : T.danger }}>
      {msg}
    </div>
  );
}

function SectionIcon({ icon: Icon, danger }) {
  return (
    <div style={{ width:32, height:32, borderRadius:8, flexShrink:0,
      background: danger ? T.dangerLight : T.accentLight,
      display:"flex", alignItems:"center", justifyContent:"center" }}>
      <Icon size={15} color={danger ? T.danger : T.accent} />
    </div>
  );
}

export default function AccountPage({ onBack }) {
  const { user } = useUser();

  const [currentPw, setCurrentPw]     = useState("");
  const [newPw, setNewPw]             = useState("");
  const [confirmPw, setConfirmPw]     = useState("");
  const [pwStatus, setPwStatus]       = useState(null);
  const [pwLoading, setPwLoading]     = useState(false);

  const [deleteEmail, setDeleteEmail]         = useState("");
  const [deleteStatus, setDeleteStatus]       = useState(null);
  const [deleteLoading, setDeleteLoading]     = useState(false);
  const [showDeleteForm, setShowDeleteForm]   = useState(false);

  const email       = user?.primaryEmailAddress?.emailAddress || "";
  const firstName   = user?.publicMetadata?.first_name || user?.firstName || "";
  const lastName    = user?.publicMetadata?.last_name  || user?.lastName  || "";
  const affiliation = user?.publicMetadata?.affiliation || "";
  const displayName = [firstName, lastName].filter(Boolean).join(" ") || email.split("@")[0];

  async function handleChangePassword(e) {
    e.preventDefault();
    if (newPw !== confirmPw) { setPwStatus({ type:"error", msg:"New passwords do not match." }); return; }
    if (newPw.length < 8)   { setPwStatus({ type:"error", msg:"Password must be at least 8 characters." }); return; }
    setPwLoading(true); setPwStatus(null);
    try {
      await user.updatePassword({ currentPassword:currentPw, newPassword:newPw });
      setPwStatus({ type:"success", msg:"Password updated successfully." });
      setCurrentPw(""); setNewPw(""); setConfirmPw("");
    } catch (err) {
      setPwStatus({ type:"error", msg: err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err.message || "Failed to update password." });
    } finally { setPwLoading(false); }
  }

  async function handleDeleteAccount(e) {
    e.preventDefault();
    if (deleteEmail !== email) { setDeleteStatus({ type:"error", msg:"Email does not match your account email." }); return; }
    setDeleteLoading(true); setDeleteStatus(null);
    try {
      await user.delete();
    } catch (err) {
      setDeleteStatus({ type:"error", msg: err?.errors?.[0]?.longMessage || err?.errors?.[0]?.message || err.message || "Failed to delete account." });
      setDeleteLoading(false);
    }
  }

  const card = {
    background:T.surface, border:`1px solid ${T.border}`,
    borderRadius:T.rLg, padding:"24px", marginBottom:20, boxShadow:T.shadow,
  };

  return (
    <div style={{ minHeight:"100vh", background:T.bg, fontFamily:font }}>

      {/* nav header */}
      <header style={{ position:"sticky", top:0, zIndex:50,
        background:T.nav, borderBottom:`1px solid ${T.navBorder}` }}>
        <div style={{ maxWidth:1200, margin:"0 auto", padding:"0 16px",
          display:"flex", alignItems:"center", height:52, gap:12 }}>
          <img src="/logo-white.png" alt="GeoSpekter"
            style={{ height:26, width:"auto", display:"block", flexShrink:0 }} />
          <div style={{ flex:1 }} />
          <button onClick={onBack} style={btnOutline}>
            <ArrowLeft size={14} /> Back
          </button>
        </div>
      </header>

      {/* content */}
      <div style={{ maxWidth:600, margin:"0 auto", padding:"36px 20px" }}>

        {/* title */}
        <div style={{ marginBottom:28 }}>
          <h1 style={{ margin:0, fontSize:22, fontWeight:700, color:T.text, fontFamily:font }}>
            Account Settings
          </h1>
          <p style={{ margin:"4px 0 0", fontSize:14, color:T.muted, fontFamily:font }}>{email}</p>
        </div>

        {/* Profile */}
        <div style={card}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
            <SectionIcon icon={User} />
            <span style={{ fontSize:14, fontWeight:600, color:T.text, fontFamily:font }}>Profile</span>
          </div>
          <ReadField label="Name" value={displayName} />
          <ReadField label="Email" value={email} />
          <ReadField label="Affiliation" value={affiliation} />
          <p style={{ margin:0, fontSize:12, color:T.muted, fontFamily:font }}>
            To update your profile details, contact us at{" "}
            <a href="mailto:admin@geospekter.com" style={{ color:T.accent, textDecoration:"none" }}>
              admin@geospekter.com
            </a>.
          </p>
        </div>

        {/* Change Password */}
        <div style={card}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:20 }}>
            <SectionIcon icon={Lock} />
            <span style={{ fontSize:14, fontWeight:600, color:T.text, fontFamily:font }}>Change Password</span>
          </div>
          <form onSubmit={handleChangePassword}>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>Current password</label>
              <input type="password" style={inp} value={currentPw}
                onChange={e => setCurrentPw(e.target.value)} required autoComplete="current-password" />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={lbl}>New password</label>
              <input type="password" style={inp} value={newPw}
                onChange={e => setNewPw(e.target.value)} required minLength={8} autoComplete="new-password" />
            </div>
            <div style={{ marginBottom:18 }}>
              <label style={lbl}>Confirm new password</label>
              <input type="password" style={inp} value={confirmPw}
                onChange={e => setConfirmPw(e.target.value)} required autoComplete="new-password" />
            </div>
            <button type="submit" style={btnPrimary} disabled={pwLoading}>
              {pwLoading ? "Updating…" : "Update password"}
            </button>
            <StatusMsg type={pwStatus?.type} msg={pwStatus?.msg} />
          </form>
        </div>

        {/* Delete Account */}
        <div style={{ ...card, borderColor:"#fca5a5" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
            <SectionIcon icon={Trash2} danger />
            <span style={{ fontSize:14, fontWeight:600, color:T.danger, fontFamily:font }}>Delete Account</span>
          </div>
          <p style={{ margin:"0 0 16px", fontSize:13, color:T.muted, lineHeight:1.6, fontFamily:font }}>
            Permanently delete your account and all associated data. This cannot be undone.
          </p>
          {!showDeleteForm ? (
            <button style={btnDanger} onClick={() => setShowDeleteForm(true)}>
              <Trash2 size={13} /> Delete my account
            </button>
          ) : (
            <form onSubmit={handleDeleteAccount}>
              <div style={{ marginBottom:16 }}>
                <label style={{ ...lbl, color:T.danger }}>
                  Type your email to confirm:{" "}
                  <span style={{ fontWeight:700, textTransform:"none", letterSpacing:0 }}>{email}</span>
                </label>
                <input type="email" style={{ ...inp, borderColor:"#fca5a5" }}
                  value={deleteEmail} onChange={e => setDeleteEmail(e.target.value)}
                  placeholder={email} required />
              </div>
              <div style={{ display:"flex", gap:10 }}>
                <button type="submit" style={btnDanger} disabled={deleteLoading}>
                  {deleteLoading ? "Deleting…" : "Confirm deletion"}
                </button>
                <button type="button" style={btnCancel}
                  onClick={() => { setShowDeleteForm(false); setDeleteEmail(""); setDeleteStatus(null); }}>
                  Cancel
                </button>
              </div>
              <StatusMsg type={deleteStatus?.type} msg={deleteStatus?.msg} />
            </form>
          )}
        </div>

      </div>
    </div>
  );
}
